import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { AIReceptionistReasonBreakdownItem } from '@/apis/agency/onboardingInsights/types';
import { BuilderEmptyStateView } from '@/components/Common/BuilderEmptyStateView';

import { AI_RECEPTIONIST_REASON_CONFIG, AI_RECEPTIONIST_REASON_ORDER } from '../../constants';

interface Props {
  data: AIReceptionistReasonBreakdownItem[];
}

export default function AIReceptionistReasonsChart({ data }: Props) {
  const chartData = useMemo(() => {
    const countByReason = data.reduce<Record<string, number>>((acc, item) => {
      acc[item.reason] = item.count;
      return acc;
    }, {});

    return AI_RECEPTIONIST_REASON_ORDER.map((reason) => ({
      name: AI_RECEPTIONIST_REASON_CONFIG[reason].label,
      value: countByReason[reason] ?? 0,
      color: AI_RECEPTIONIST_REASON_CONFIG[reason].chartColor
    }));
  }, [data]);

  const isAllZero = chartData.every((item) => item.value === 0);

  return (
    <Box bgcolor='#fff' border='1px solid #ECECEC' borderRadius={2} p={2} mt={2}>
      <Typography fontSize={18} fontWeight={500}>
        Reasons for Choosing AI Receptionist
      </Typography>
      <Typography variant='body2' color='#777' mt={0.5} mb={2}>
        Overview of key reasons for choosing an AI Receptionist
      </Typography>

      {isAllZero ? (
        <Box py={8}>
          <BuilderEmptyStateView />
        </Box>
      ) : (
        <ResponsiveContainer width='100%' height={480}>
          <BarChart data={chartData} layout='vertical' margin={{ left: 20, right: 30, top: 0, bottom: 0 }}>
            <XAxis type='number' allowDecimals={false} />
            <YAxis
              type='category'
              dataKey='name'
              width={260}
              interval={0}
              tick={({ x, y, payload }) => (
                <text x={x} y={y} dy={4} textAnchor='end' fontSize={12} fill='#666'>
                  {payload.value}
                </text>
              )}
            />
            <Tooltip />
            <Bar dataKey='value' radius={[0, 8, 8, 0]} barSize={29}>
              {chartData.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
              <LabelList dataKey='value' position='right' style={{ fill: '#6b7280', fontSize: 12 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
}
